import { Controller } from "@hotwired/stimulus"

// Name-token controller — resolves the [FirstName] tokens the section
// labels are rendered with (see NativeEditorHelper#name_tokenize). Shows
// the participant's real first name when Redact PHI is OFF, and keeps the
// literal "[FirstName]" tag when Redact is ON (matching the redacted
// PDF/preview) or before any name has been entered. Lives on the same
// <body> element as plan-store and listens to the shared plan:change
// event, so labels update the instant the name field or the redact toggle
// changes. firstName resolution mirrors the print preview's
// _expandPlaceholders (firstName field → first word of full name → tag).
const STORAGE_KEY = "pcp_plan_data"
const TOKEN = "[FirstName]"

export default class extends Controller {
  connect() {
    this._onChange = (e) => {
      const p = e.detail?.path
      // Only re-resolve for changes that can affect the label text: the
      // participant's name, the redact flag, or a whole-plan swap
      // (undo/redo, open .sdp, new/sample plan → path "*"). Skips the
      // per-keystroke churn from every other field.
      if (p && p !== "*" && p !== "client.firstName" && p !== "client.name" && p !== "meta.redacted") return
      this.plan = e.detail?.plan || this._load()
      this._resolve()
    }
    document.addEventListener("plan:change", this._onChange)
    // Cross-tab: pick up name / redact changes made in another tab.
    this._onStorage = (e) => {
      if (e.key !== STORAGE_KEY || e.newValue == null) return
      try { this.plan = JSON.parse(e.newValue) || {} } catch (_) { return }
      this._resolve()
    }
    window.addEventListener("storage", this._onStorage)
    this.plan = this._load()
    this._resolve()
    // Editor sections are lazy-mounted (editor-native clones a <template
    // data-lazy-content> on first navigation) and the wizard pane rebuilds
    // its innerHTML on every step — both inject fresh [data-name-token]
    // spans AFTER connect with no plan:change to trigger a resolve. Watch
    // the subtree so newly-mounted spans resolve immediately (mirrors
    // plan-store's re-hydrate observer). _resolve is idempotent — the
    // `textContent !== text` guard skips no-op writes, so our own updates
    // don't feed the observer into a loop.
    this.observer = new MutationObserver(() => this._resolve())
    this.observer.observe(this.element, {
      childList: true, subtree: true, attributes: true, attributeFilter: ["hidden"]
    })
  }

  disconnect() {
    document.removeEventListener("plan:change", this._onChange)
    window.removeEventListener("storage", this._onStorage)
    if (this.observer) this.observer.disconnect()
  }

  _resolve() {
    const c = (this.plan && this.plan.client) || {}
    const redacted = !!(this.plan && this.plan.meta && this.plan.meta.redacted)
    const first = (c.firstName && c.firstName.trim()) ||
                  (c.name && c.name.trim() && c.name.trim().split(/\s+/)[0]) ||
                  ""
    // Redact ON (or no name yet) → keep the tag; OFF → the real name.
    const text = (redacted || !first) ? TOKEN : first
    this.element.querySelectorAll("[data-name-token]").forEach(el => {
      if (el.textContent !== text) el.textContent = text
    })
  }

  _load() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      return raw ? JSON.parse(raw) : {}
    } catch (_) { return {} }
  }
}
